/**
 * Session Timeout Hook for CFR 21 Part 11 Compliance
 *
 * Behavior:
 * - OAuth users (Google/Microsoft): No automatic timeout
 * - Password users: Logout at midnight local time
 * - Shows warning 10 minutes before midnight for password users
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export type AuthMethod = 'password' | 'google' | 'azure' | null;

interface SessionTimeoutState {
  isWarningVisible: boolean;
  minutesRemaining: number;
  shouldLogout: boolean;
}

interface UseSessionTimeoutOptions {
  authMethod: AuthMethod;
  onLogout: () => void;
  warningMinutes?: number; // How many minutes before midnight to show warning
}

/**
 * Get time until midnight in milliseconds
 */
function getTimeUntilMidnight(): number {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0); // Next midnight
  return midnight.getTime() - now.getTime();
}

/**
 * Get time until midnight in minutes
 */
function getMinutesUntilMidnight(): number {
  return Math.ceil(getTimeUntilMidnight() / (1000 * 60));
}

export function useSessionTimeout({
  authMethod,
  onLogout,
  warningMinutes = 10,
}: UseSessionTimeoutOptions): SessionTimeoutState {
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(getMinutesUntilMidnight());
  const [shouldLogout, setShouldLogout] = useState(false);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const setupTimers = useCallback(() => {
    // Only set up timers for password authentication
    if (authMethod !== 'password') {
      setIsWarningVisible(false);
      setShouldLogout(false);
      return;
    }

    const msUntilMidnight = getTimeUntilMidnight();
    const msUntilWarning = msUntilMidnight - warningMinutes * 60 * 1000;

    // Set up warning timer (10 minutes before midnight)
    if (msUntilWarning > 0) {
      warningTimerRef.current = setTimeout(() => {
        setIsWarningVisible(true);
        // Start countdown interval when warning shows
        countdownIntervalRef.current = setInterval(() => {
          const mins = getMinutesUntilMidnight();
          setMinutesRemaining(mins);
          if (mins <= 0) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
            }
          }
        }, 30000); // Update every 30 seconds
      }, msUntilWarning);
    } else if (msUntilMidnight > 0) {
      // Already within warning period
      setIsWarningVisible(true);
      setMinutesRemaining(getMinutesUntilMidnight());
      countdownIntervalRef.current = setInterval(() => {
        const mins = getMinutesUntilMidnight();
        setMinutesRemaining(mins);
        if (mins <= 0) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
        }
      }, 30000);
    }

    // Set up logout timer (at midnight)
    logoutTimerRef.current = setTimeout(() => {
      setShouldLogout(true);
      setIsWarningVisible(false);
      onLogout();
    }, msUntilMidnight);
  }, [authMethod, onLogout, warningMinutes]);

  useEffect(() => {
    clearAllTimers();
    setupTimers();

    return () => {
      clearAllTimers();
    };
  }, [authMethod, setupTimers, clearAllTimers]);

  // Reset timers at midnight if user is still around (edge case)
  useEffect(() => {
    if (shouldLogout && authMethod === 'password') {
      // Give a small buffer then set up next day's timers
      const resetTimer = setTimeout(() => {
        setShouldLogout(false);
        setupTimers();
      }, 1000);
      return () => clearTimeout(resetTimer);
    }
  }, [shouldLogout, authMethod, setupTimers]);

  return {
    isWarningVisible,
    minutesRemaining,
    shouldLogout,
  };
}

/**
 * Determine auth method from Supabase session
 */
export function getAuthMethodFromSession(session: any): AuthMethod {
  if (!session?.user) return null;

  const provider = session.user.app_metadata?.provider;

  if (provider === 'google') return 'google';
  if (provider === 'azure') return 'azure';
  if (provider === 'email' || !provider) return 'password';

  // Default to password for any other provider
  return 'password';
}
